import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Favicon } from '../../resources/assets/images/favicons';
import AppIcon from '../../resources/assets/images/AppIcon.png';
import useBrand from '../../hooks/context/useBrand';

// The static <title> from index.html — restored when the brand is FoodAmigos.
const DEFAULT_TITLE = 'Restaurant dashboard';

// The rels react-smartbanner looks through for the banner icon, and the ones a
// phone pins to its home screen.
const TOUCH_ICON_RELS = ['apple-touch-icon', 'android-touch-icon'];

// What a white-label brand without its own logo wears in the tab: a plain dot
// in its colour — never the FoodAmigos mark. Mirrors oms-web's fallback.
const NEUTRAL_ICON_COLOR = '#161616';

function buildFallbackIcon(primaryColor) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="${primaryColor}"/></svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// Reuses the link of that rel if the shell already has one (index.html ships a
// blank favicon placeholder), so a second link never competes with it.
function setHeadLink(rel, href) {
  const link = document.head.querySelector(`link[rel="${rel}"]`) ?? document.createElement('link');
  link.setAttribute('rel', rel);
  link.setAttribute('href', href);

  if (!link.isConnected) {
    document.head.appendChild(link);
  }
}

export default function MetaInfo() {
  const brand = useBrand();

  const isWhiteLabel = brand?.isWhiteLabel;
  const brandIcon = isWhiteLabel ? brand.circleLogoUrl || brand.logoUrl || null : null;
  const icon = isWhiteLabel ? (brandIcon ?? buildFallbackIcon(brand.primaryColor ?? NEUTRAL_ICON_COLOR)) : Favicon;

  // Home-screen icon. Rendered here rather than in index.html because a static
  // FoodAmigos icon would win the first paint on a partner host, and a second
  // <link rel="apple-touch-icon"> cannot reliably override the first.
  const touchIcon = isWhiteLabel ? icon : AppIcon;

  // Link previews need a real image; the fallback dot is a data URI no preview
  // can use.
  const previewImage = isWhiteLabel ? brandIcon : Favicon;

  // Written imperatively rather than through Helmet, which defers its DOM
  // writes to requestAnimationFrame — a frame later than any effect.
  // SmartAppBanner's banner icon is read out of these very links, once, during
  // SmartBanner's own render, so that frame is the difference between an icon
  // and an empty square.
  useEffect(() => {
    TOUCH_ICON_RELS.forEach((rel) => setHeadLink(rel, touchIcon));
  }, [touchIcon]);

  // Imperative for the same reason: Helmet's late write let the shell's icon
  // (and Safari's cached /favicon.ico) win the partner's tab.
  useEffect(() => {
    setHeadLink('icon', icon);
  }, [icon]);

  // react-helmet's <title> does not reliably win over the static index.html
  // title under React 19, so the tab title is set imperatively.
  useEffect(() => {
    document.title = isWhiteLabel ? brand.name : DEFAULT_TITLE;
  }, [isWhiteLabel, brand?.name]);

  useEffect(() => {
    if (!isWhiteLabel) {
      return;
    }

    const manifest = {
      name: brand.name,
      short_name: brand.name,
      start_url: '/',
      display: 'standalone',
      theme_color: brand.primaryColor,
      background_color: '#ffffff',
      icons: brandIcon ? [{ src: brandIcon, sizes: '512x512', type: 'image/png' }] : [],
    };

    const url = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/json' }));
    document.querySelector('link[rel="manifest"]')?.setAttribute('href', url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [isWhiteLabel, brand?.name, brand?.primaryColor, brandIcon]);

  return (
    <div className="application">
      <Helmet>
        <meta charSet="utf-8" />
        {previewImage && <meta property="og:image" content={previewImage} />}
        {previewImage && <meta name="twitter:image" content={previewImage} />}
      </Helmet>
    </div>
  );
}
